import { makeAutoObservable, runInAction } from "mobx";
import { apiClient } from "../utils/apiClient";
import { authStore } from "./AuthStore";
import { CachedBooking, TAvailability, TCalendarBooking } from "../types/types";

const CACHE_EXPIRY_TIME = 60 * 1000; // 60 * ms
const CACHE_KEY_PREFIX = "booking_cache_";
const AVAILABILITY_CACHE_KEY_PREFIX = "availability_cache_";

export interface BookingStoreInterface {
  calendarBookings: TCalendarBooking[];
  calendarAvailability: TAvailability[];
  bookings: TCalendarBooking[];
  pagination: { limit: number; offset: number } | null;
  isLoading: boolean;
  error: string | null;
  fetchCalendarBookings: (
    from: string,
    to: string,
    establishmentId: number,
  ) => Promise<void>;
  saveBooking: (booking: TCalendarBooking) => Promise<any>;
}

class BookingStore implements BookingStoreInterface {
  calendarBookings: TCalendarBooking[] = [];
  calendarAvailability: TAvailability[] = [];
  bookings: TCalendarBooking[] = [];
  pagination: { limit: number; offset: number } | null = null;
  isLoading = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Generate cache key from date range and establishment ID
   */
  private getCacheKey(from: string, to: string): string {
    return `${CACHE_KEY_PREFIX}_${from}_${to}`;
  }

  /**
   * Get cached bookings if they're still valid
   */
  private getCachedBookings(
    from: string,
    to: string,
  ): TCalendarBooking[] | null {
    try {
      const cacheKey = this.getCacheKey(from, to);
      const cached = localStorage.getItem(cacheKey);

      if (!cached) {
        return null;
      }

      const cachedData: CachedBooking = JSON.parse(cached);
      const now = Date.now();
      const cacheAge = now - cachedData.timestamp;

      // Check if cache is still valid (less than 1 minute old)
      if (cacheAge < CACHE_EXPIRY_TIME) {
        //console.log(`📦 Using cached bookings (${Math.round(cacheAge / 1000)}s old)`);
        return cachedData.data;
      } else {
        // Cache expired, remove it
        localStorage.removeItem(cacheKey);
        return null;
      }
    } catch (err) {
      console.error("Error reading cache:", err);
      return null;
    }
  }

  /**
   * Get cached availability if it's still valid
   */
  private getCachedAvailability(
    from: string,
    to: string,
  ): any | null {
    try {
      const cacheKey = `${AVAILABILITY_CACHE_KEY_PREFIX}_${from}_${to}`;
      const cached = localStorage.getItem(cacheKey);

      if (!cached) {
        return null;
      }

      const cachedData: CachedBooking = JSON.parse(cached);
      const now = Date.now();
      const cacheAge = now - cachedData.timestamp;

      // Check if cache is still valid (less than 1 minute old)
      if (cacheAge < CACHE_EXPIRY_TIME) {
        return cachedData.data;
      } else {
        // Cache expired, remove it
        localStorage.removeItem(cacheKey);
        return null;
      }
    } catch (err) {
      console.error("Error reading availability cache:", err);
      return null;
    }
  }

  /**
   * Store availability in cache
   */
  private setCachedAvailability(from: string, to: string, data: any): void {
    try {
      const cacheKey = `${AVAILABILITY_CACHE_KEY_PREFIX}_${from}_${to}`;
      const cachedData: CachedBooking = {
        data,
        timestamp: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(cachedData));
    } catch (err) {
      console.error("Error writing availability cache:", err);
    }
  }

  /**
   * Store bookings in cache
   */
  private setCachedBookings(from: string, to: string, data: any): void {
    try {
      const cacheKey = this.getCacheKey(from, to);
      const cachedData: CachedBooking = {
        data,
        timestamp: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(cachedData));
      //console.log(`💾 Cached bookings for ${from} to ${to}`);
    } catch (err) {
      console.error("Error writing cache:", err);
    }
  }

  /**
   * Fetch calendar bookings for a date range and establishment
   * @param from Start date (YYYY-MM-DD)
   * @param to End date (YYYY-MM-DD)
   * @param establishmentId The establishment ID to fetch bookings for
   */
  async fetchCalendarBookings(from: string, to: string): Promise<void> {
    console.log('Fetching calendar bookings for', from, 'to', to);
    const establishmentId = this.getEstablishmentId();
    if (!establishmentId) {
      authStore.logout();
      return;
    }
    this.isLoading = true;
    this.error = null;

    try {
      // Check cache first
      const cachedData = this.getCachedBookings(from, to);
      if (cachedData) {
        runInAction(() => {
          this.calendarBookings = cachedData;
          this.isLoading = false;
        });
        return;
      }

      const response = await apiClient.post(`/admin/calendar-bookings`, {
        from,
        to,
        establishment_id: establishmentId,
      });

      const bookings = response.data || [];
      bookings.forEach((booking: TCalendarBooking) => {
        try {
          //console.log("booking.pricing_structure", booking.pricing_structure)
          if (booking.pricing_structure) {
            booking.pricing_structure_json = JSON.parse(
              booking.pricing_structure,
            );

            delete booking.pricing_structure; // Remove the original string if you only want the parsed object
          }
        } catch (err) {
          console.error(
            `Error parsing pricing structure for booking ${booking.booking_id}:`,
            err,
          );
        }
      });
      this.setCachedBookings(from, to, bookings);

      runInAction(() => {
        this.calendarBookings = bookings;
      });
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error
            ? err.message
            : "Failed to fetch calendar bookings";
        console.error("Calendar bookings error:", err);
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async fetchAvailability(from: string, to: string): Promise<void> {
    // console.log('Fetching availability for', from, 'to', to);
    const establishmentId = this.getEstablishmentId();
    if (!establishmentId) {
      authStore.logout();
      return;
    }
    this.isLoading = true;
    this.error = null;

    try {
      // Check cache first
      const cachedData = this.getCachedAvailability(from, to);
      if (cachedData) {
        runInAction(() => {
          this.calendarAvailability = cachedData;
          this.isLoading = false;
        });
        return;
      }

      const response = await apiClient.post(`/admin/calendar-availability`, {
        from,
        to,
        establishment_id: establishmentId,
      });

      const availability = response.data || [];
      this.setCachedAvailability(from, to, availability);

      runInAction(() => {
        this.calendarAvailability = availability;
      });
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error
            ? err.message
            : "Failed to fetch calendar availability";
        console.error("Calendar availability error:", err);
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Clear calendar bookings
   */
  clearCalendarBookings(): void {
    this.calendarBookings = [];
    this.error = null;
  }

  /**
   * Save a booking (create or update)
   * @param booking The booking to save
   */
  async saveBooking(booking: TCalendarBooking): Promise<any> {
    this.error = null;

    const establishmentId = this.getEstablishmentId();
    if (!establishmentId) {
      authStore.logout();
      return;
    }

    try {
      if (booking.booking_id) {
        // Update existing booking
        const response = await apiClient.put(
          `/bookings/${booking.booking_id}`,
          booking,
        );
        return response.data;
      } else {
        // Create new booking
        //@ts-ignore
        booking.establishment_id = establishmentId; // Ensure establishment_id is set
        const response = await apiClient.post("/admin/bookings", booking);
        return response.data;
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message || "Failed to save booking";
      runInAction(() => {
        this.error = errorMessage;
      });
      throw new Error(errorMessage);
    }
  }

  async capturePayment(
    booking: TCalendarBooking,
    amount: number,
    reference: string,
  ): Promise<any> {
    const establishmentId = this.getEstablishmentId();
    if (!establishmentId) {
      authStore.logout();
      return;
    }
    this.error = null;

    try {
      const response = await apiClient.post(
        `/admin/bookings/${booking.booking_id}/payments`,
        {
          establishment_id: establishmentId,
          amount: amount,
          reference: reference,
        },
      );
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message || "Failed to mark booking paid";
      runInAction(() => {
        this.error = errorMessage;
      });
      throw new Error(errorMessage);
    }
  }

  async cancelBooking(booking: TCalendarBooking): Promise<any> {
    const establishmentId = this.getEstablishmentId();
    if (!establishmentId) {
      authStore.logout();
      return;
    }
    this.error = null;

    try {
      const response = await apiClient.post(
        `/admin/bookings/cancel/${booking.booking_id}`,
        {
          establishment_id: establishmentId,
        }
      );
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message || "Failed to cancel booking";
      runInAction(() => {
        this.error = errorMessage;
      });
      throw new Error(errorMessage);
    }
  }

  async confirmBooking(booking: TCalendarBooking): Promise<any> {
    const establishmentId = this.getEstablishmentId();
    if (!establishmentId) {
      authStore.logout();
      return;
    }
    this.error = null;

    try {
      const response = await apiClient.post(
        `/admin/bookings/${booking.booking_id}/confirm`,
        {
          establishment_id: establishmentId,
        }
      );
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message || "Failed to confirm booking";
      runInAction(() => {
        this.error = errorMessage;
      });
      throw new Error(errorMessage);
    }
  }

  clearCache(from: string, to: string): void {
    const cacheKey = this.getCacheKey(from, to);
    localStorage.removeItem(cacheKey);
    //console.log(`🧹 Cleared cache for ${from} to ${to}`);
  }

  clearAvailabilityCache(from: string, to: string): void {
    const cacheKey = `${AVAILABILITY_CACHE_KEY_PREFIX}_${from}_${to}`;
    localStorage.removeItem(cacheKey);
  }

  /**
   * List bookings with pagination
   * @param status Optional status filter
   * @param limit Pagination limit
   * @param offset Pagination offset
   */
  async listBookings(
    limit: number = 100,
    offset: number = 0,
    filters: any = {}
  ): Promise<void> {
    const establishmentId = this.getEstablishmentId();
    if (!establishmentId) {
      authStore.logout();
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      const params = new URLSearchParams({
        establishment_id: establishmentId.toString(),
        limit: limit.toString(),
        offset: offset.toString(),
        ...filters,
      });

      const response = await apiClient.get(`/admin/bookings?${params.toString()}`);
      const result = response.data || {};

      runInAction(() => {
        this.bookings = result.data || [];
        this.pagination = result.pagination || { limit, offset };
      });
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : "Failed to fetch bookings";
        console.error("Bookings error:", err);
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  getEstablishmentId(): number {
    const establishmentId =
      authStore.establishmentId || parseInt(localStorage.getItem("establishmentId") ?? "0") || 0;
    return establishmentId;
  }
}

export const bookingStore = new BookingStore();
